import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * ボタン（docs/09-design-system.md 5.2）。
 * バリアントの追加は cva 定義に集約する。
 * サイズ：sm=32px（PC の一覧内のみ）／default=40px／lg=44px（スマホの主要操作）。
 */
const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control text-button',
    'transition-colors duration-hover ease-out',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-5',
  ),
  {
    variants: {
      variant: {
        /** 主要操作（送信を開始、保存） */
        default: 'bg-primary text-primary-foreground hover:opacity-90',
        /** 副次操作（キャンセル、条件をリセット） */
        secondary: 'bg-muted text-foreground hover:bg-border',
        /** 一覧内の操作（詳細、編集） */
        outline: 'border border-border-strong bg-background text-foreground hover:bg-muted',
        /** アイコンボタン、密度の高い場所 */
        ghost: 'text-foreground hover:bg-muted',
        /** 削除、全停止。確認ダイアログ必須 */
        destructive: 'bg-primary text-primary-foreground font-semibold hover:opacity-90',
        /** 遷移のみ */
        link: 'text-foreground underline underline-offset-4 hover:opacity-80',
      },
      // スマホでは 44px を確保し、PC で本来の高さに戻す。
      // sm は「PC の一覧内のみ」の想定のため、スマホでは 44px になる（P05）。
      size: {
        sm: 'h-touch px-3 text-note lg:h-8',
        default: 'h-touch px-4 py-2.5 lg:h-10',
        lg: 'h-touch px-5 py-3',
        icon: 'size-touch lg:size-10',
        'icon-sm': 'size-touch lg:size-8',
        'icon-lg': 'size-touch',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** 非活性の理由。ツールチップで表示する（docs/09-design-system.md 第8章） */
    disabledReason?: string;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, disabledReason, disabled, title, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled}
        title={disabled && disabledReason ? disabledReason : title}
        aria-disabled={disabled || undefined}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
